/**
 * metaAdsShared.js — etiquetas y formatos que comparten las piezas de Meta Ads
 * del tablero de Campañas (resumen de cuenta, análisis por pestañas, detalle de
 * campaña). Meta entrega enums en inglés (ACTIVE, LINK_CLICKS, ABOVE_AVERAGE…);
 * aquí se traducen UNA vez para que todas las vistas digan lo mismo.
 */

export const numFmt = new Intl.NumberFormat('es-MX');
export const compact = (n) => (n == null ? '—' : new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(n));
export const pct = (n, d = 2) => (n == null ? '—' : `${(n * 100).toFixed(d)}%`);
export const dec = (n, d = 2) => (n == null ? '—' : Number(n).toFixed(d));

/** Formateador de moneda con la divisa de la cuenta (no se asume MXN). */
export function makeMoney(currency = 'MXN') {
  let fmt;
  let fmt2;
  try {
    fmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 0 });
    fmt2 = new Intl.NumberFormat('es-MX', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (_) {
    fmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
    fmt2 = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
  }
  const money = (n) => (n == null ? '—' : fmt.format(Math.round(n)));
  // Costos unitarios (CPC, costo por resultado) necesitan centavos: $0.45 no es $0.
  money.unit = (n) => (n == null ? '—' : (Math.abs(n) < 100 ? fmt2.format(n) : fmt.format(Math.round(n))));
  return money;
}

/** "1,234 interacciones · $0.45 c/u" — el resultado con su costo, en una línea. */
export function resultLine(r, money) {
  if (!r || r.value == null) return null;
  const cost = r.cost != null ? `${money.unit(r.cost)} ${r.perMil ? 'por mil' : 'c/u'}` : null;
  return { value: compact(r.value), label: (r.label || '').toLowerCase(), cost };
}

export const STATUS_LABEL = {
  ACTIVE: 'Activa', PAUSED: 'Pausada', CAMPAIGN_PAUSED: 'Campaña pausada',
  ADSET_PAUSED: 'Conjunto pausado', ARCHIVED: 'Archivada', DELETED: 'Eliminada',
  IN_PROCESS: 'En proceso', WITH_ISSUES: 'Con problemas', PENDING_REVIEW: 'En revisión',
  DISAPPROVED: 'Rechazado', PREAPPROVED: 'Preaprobado', PENDING_BILLING_INFO: 'Falta pago',
  COMPLETED: 'Finalizada',
};
export const statusTone = (s) => {
  const v = (s || '').toUpperCase();
  if (v === 'ACTIVE') return 'ok';
  if (['WITH_ISSUES', 'DISAPPROVED', 'PENDING_BILLING_INFO'].includes(v)) return 'bad';
  if (['IN_PROCESS', 'PENDING_REVIEW', 'PREAPPROVED'].includes(v)) return 'warn';
  return 'off';
};

export const OPT_GOAL_LABEL = {
  POST_ENGAGEMENT: 'Interacciones', ENGAGED_USERS: 'Personas que interactúan',
  LINK_CLICKS: 'Clics en el enlace', LANDING_PAGE_VIEWS: 'Vistas de la página de destino',
  REACH: 'Alcance', AD_RECALL_LIFT: 'Recordación del anuncio', IMPRESSIONS: 'Impresiones',
  THRUPLAY: 'ThruPlays', TWO_SECOND_CONTINUOUS_VIDEO_VIEWS: 'Reproducciones de 2 s',
  LEAD_GENERATION: 'Prospectos', QUALITY_LEAD: 'Prospectos de calidad',
  OFFSITE_CONVERSIONS: 'Conversiones', VALUE: 'Valor de conversión',
  CONVERSATIONS: 'Conversaciones', PAGE_LIKES: 'Me gusta de la Página', PROFILE_VISIT: 'Visitas al perfil',
};

export const BID_LABEL = {
  LOWEST_COST_WITHOUT_CAP: 'Menor costo (automática)',
  LOWEST_COST_WITH_BID_CAP: 'Límite de puja',
  COST_CAP: 'Límite de costo',
  LOWEST_COST_WITH_MIN_ROAS: 'ROAS mínimo',
};

export const BUY_LABEL = { AUCTION: 'Subasta', RESERVED: 'Alcance y frecuencia', FIXED_CPM: 'CPM fijo' };

export const OBJECTIVE_LABEL = {
  OUTCOME_ENGAGEMENT: 'Interacción', OUTCOME_TRAFFIC: 'Tráfico', OUTCOME_AWARENESS: 'Reconocimiento',
  OUTCOME_LEADS: 'Clientes potenciales', OUTCOME_SALES: 'Ventas', OUTCOME_APP_PROMOTION: 'Promoción de app',
  POST_ENGAGEMENT: 'Interacción', LINK_CLICKS: 'Tráfico', REACH: 'Alcance', BRAND_AWARENESS: 'Reconocimiento',
  VIDEO_VIEWS: 'Reproducciones de video', MESSAGES: 'Mensajes', CONVERSIONS: 'Conversiones',
  LEAD_GENERATION: 'Clientes potenciales', PAGE_LIKES: 'Me gusta de la Página',
};

export const CTA_LABEL = {
  LEARN_MORE: 'Más información', SHOP_NOW: 'Comprar', SIGN_UP: 'Registrarte', CONTACT_US: 'Contáctanos',
  SEND_MESSAGE: 'Enviar mensaje', WHATSAPP_MESSAGE: 'WhatsApp', BOOK_TRAVEL: 'Reservar', GET_OFFER: 'Obtener oferta',
  DOWNLOAD: 'Descargar', WATCH_MORE: 'Ver más', APPLY_NOW: 'Solicitar', SUBSCRIBE: 'Suscribirte',
  ORDER_NOW: 'Pedir ahora', GET_QUOTE: 'Cotizar', CALL_NOW: 'Llamar', LIKE_PAGE: 'Me gusta', NO_BUTTON: 'Sin botón',
  INSTAGRAM_MESSAGE: 'Mensaje de Instagram', VIEW_INSTAGRAM_PROFILE: 'Ver perfil',
};

export const FORMAT_LABEL = {
  VIDEO: 'Video', PHOTO: 'Imagen', SHARE: 'Enlace', STATUS: 'Publicación', OFFER: 'Oferta',
  INVALID: 'Otro', APPLICATION: 'App', EVENT: 'Evento', MULTI_SHARE: 'Carrusel',
};

// Rankings de calidad de Meta: comparan el anuncio contra los que compitieron
// por la misma audiencia. Solo existen con ~500 impresiones o más.
export const RANK_INFO = {
  ABOVE_AVERAGE: { label: 'Superior al promedio', tone: 'ok' },
  AVERAGE: { label: 'Promedio', tone: 'mid' },
  BELOW_AVERAGE_35: { label: 'Inferior (35 % más bajo)', tone: 'warn' },
  BELOW_AVERAGE_20: { label: 'Inferior (20 % más bajo)', tone: 'bad' },
  BELOW_AVERAGE_10: { label: 'Inferior (10 % más bajo)', tone: 'bad' },
};
export const RANKS = [
  { key: 'quality_ranking', label: 'Calidad' },
  { key: 'engagement_rate_ranking', label: 'Interacción' },
  { key: 'conversion_rate_ranking', label: 'Conversión' },
];

export const label = (map, v) => map[v] || (v ? String(v).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : '—');

export const shortDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return iso || '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');
};
